import { trpc } from '@tencent/tpro_rmonitor_web_server_network_http';
import { get } from 'lodash-es';
import AbstractHttpClient from '@/infrastructure/axios/request';
import { TrpcService } from '@/service/trpc';
import { WithTypeCast } from '@raft/utils';

export type IGetErrURLListReq = WithTypeCast<trpc.rmonitor.web_server.IGetErrURLListReq>;
export type IGetErrURLListRsp = WithTypeCast<trpc.rmonitor.web_server.IGetErrURLListRsp>;
export type IProblemInfo = WithTypeCast<trpc.rmonitor.web_server.GetIssueListRsp.IProblemInfo>;
export type IErrURLInfo = WithTypeCast<trpc.rmonitor.web_server.GetErrURLListRsp.IErrURLInfo>;
export type IGetIssueListRsp = WithTypeCast<trpc.rmonitor.web_server.IGetIssueListRsp>;
export type IDimensionalGroups = WithTypeCast<trpc.rmonitor.web_server.IDimensionalGroups>;
export type IGetMultiDimensionalGroupsReq = WithTypeCast<trpc.rmonitor.web_server.IGetMultiDimensionalGroupsReq>;
export type IGetIssueListReq = WithTypeCast<trpc.rmonitor.web_server.IGetIssueListReq>;
export type IGetReportListReq = WithTypeCast<trpc.rmonitor.web_server.IGetReportListReq>;
export type IGetReportListRsp = WithTypeCast<trpc.rmonitor.web_server.IGetReportListRsp>;
export type IAccounts = WithTypeCast<trpc.rmonitor.web_server.GetReportListRsp.IAccounts>;
export type IGetDetailReq = WithTypeCast<trpc.rmonitor.web_server.IGetDetailReq>;
export type IGetDetailRsp = WithTypeCast<trpc.rmonitor.web_server.IGetDetailRsp>;
export type IProblemDetail = WithTypeCast<trpc.rmonitor.web_server.GetDetailRsp.IProblemDetail>;
export type IGetDomainStatsReq = WithTypeCast<trpc.rmonitor.web_server.IGetDomainStatsReq>;
export type IGetDomainStatsRsp = WithTypeCast<trpc.rmonitor.web_server.IGetDomainStatsRsp>;

// ============================================================================
// 新增接口类型（@tencent/tpro_rmonitor_web_server_network_http@1.7.3）
// ============================================================================
export type IGetTraceInfoForAPIReq = WithTypeCast<trpc.rmonitor.web_server.IGetTraceInfoForAPIReq>;
export type IGetTraceInfoForAPIRsp = WithTypeCast<trpc.rmonitor.web_server.IGetTraceInfoForAPIRsp>;
export type IGetNetworkMetricThresholdsReq = WithTypeCast<trpc.rmonitor.web_server.IGetNetworkMetricThresholdsReq>;
export type IGetNetworkMetricThresholdsRsp = WithTypeCast<trpc.rmonitor.web_server.IGetNetworkMetricThresholdsRsp>;
export type IGetDiagnoseMetricsReq = WithTypeCast<trpc.rmonitor.web_server.IGetDiagnoseMetricsReq>;
export type IGetDiagnoseMetricsRsp = WithTypeCast<trpc.rmonitor.web_server.IGetDiagnoseMetricsRsp>;
export type IGetDiagnoseTrendReq = WithTypeCast<trpc.rmonitor.web_server.IGetDiagnoseTrendReq>;
export type IGetDiagnoseTrendRsp = WithTypeCast<trpc.rmonitor.web_server.IGetDiagnoseTrendRsp>;
export type IGetDiagnoseListReq = WithTypeCast<trpc.rmonitor.web_server.IGetDiagnoseTrendTableReq>;
export type IGetDiagnoseListRsp = WithTypeCast<trpc.rmonitor.web_server.IGetDiagnoseTrendTableRsp>;
export type IGetDiagnoseDetailReq = WithTypeCast<trpc.rmonitor.web_server.IGetDiagnoseDetailReq>;
export type IGetDiagnoseDetailRsp = WithTypeCast<trpc.rmonitor.web_server.IGetDiagnoseDetailRsp>;
export type IDiagnoseTrendRow = WithTypeCast<trpc.rmonitor.web_server.IDiagnoseTrendRow>;
export type IDiagnoseDetailRecord = WithTypeCast<trpc.rmonitor.web_server.IDiagnoseDetailRecord>;
export type DiagnoseTrendDimension = trpc.rmonitor.web_server.DiagnoseTrendDimension;
export const { DiagnoseTrendDimension } = trpc.rmonitor.web_server;
export type IDiagnoseTrendSeries = trpc.rmonitor.web_server.GetDiagnoseTrendRsp.IDiagnoseTrendSeries;

/**
 * 产品成员管理
 */
export class NetHttpService extends AbstractHttpClient {
  constructor() {
    super({
      serviceName: TrpcService.NetHttpService,
    });
  }

  /**
   * 获取多维分组
   * @param req
   */
  public async getMultiDimensionalGroups(req: IGetMultiDimensionalGroupsReq): Promise<IDimensionalGroups[]> {
    try {
      const res = await this.trpcPost('GetMultiDimensionalGroups', req);
      return get(res, 'groups', []);
    } catch (e) {
      console.log('getMultiDimensionalGroups', e);
      return [];
    }
  }

  public async getIssueList(req: IGetIssueListReq): Promise<IGetIssueListRsp> {
    try {
      return await this.trpcPost('GetIssueList', req);
    } catch (e) {
      return {
        topCaseRatio: 0,
        data: [],
        totalIssueCount: 0,
        topIssueCount: 0,
      };
    }
  }

  public async getReportList(req: IGetReportListReq): Promise<IGetReportListRsp> {
    try {
      return await this.trpcPost('GetReportList', req);
    } catch (e) {
      return {
        accounts: [],
      };
    }
  }

  public async getDetail(req: IGetDetailReq): Promise<IGetDetailRsp> {
    try {
      return await this.trpcPost('GetDetail', req);
    } catch (e) {
      return {
        problemDetail: null,
      };
    }
  }

  public async getErrURLList(req: IGetErrURLListReq): Promise<IGetErrURLListRsp> {
    try {
      return await this.trpcPost('GetErrURLList', req);
    } catch (e) {
      return {
        totalDocCount: 0,
        data: [],
        totalQimeiCount: 0,
        totalAccountCount: 0,
      };
    }
  }

  // ==========================================================================
  // 以下为 @tencent/tpro_rmonitor_web_server_network_http@1.7.3 新增接口
  // ==========================================================================

  /**
   * 获取 API 链路追踪信息
   */
  public async getTraceInfoForAPI(req: IGetTraceInfoForAPIReq): Promise<IGetTraceInfoForAPIRsp> {
    try {
      return await this.trpcPost('GetTraceInfoForAPI', req);
    } catch (e) {
      return { spans: [], baseRsp: { code: 0, msg: '' } };
    }
  }

  /**
   * 获取网络指标阈值配置
   */
  public async getNetworkMetricThresholds( // eslint-disable-line function-paren-newline
    req: IGetNetworkMetricThresholdsReq,
  ): Promise<IGetNetworkMetricThresholdsRsp> { // eslint-disable-line function-paren-newline
    try {
      return await this.trpcPost('GetNetworkMetricThresholds', req);
    } catch (e) {
      return {  };
    }
  }

  /**
   * 获取拨测指标数据
   */
  public async getDiagnoseMetrics(req: IGetDiagnoseMetricsReq): Promise<IGetDiagnoseMetricsRsp> {
    try {
      return await this.trpcPost('GetDiagnoseMetrics', req);
    } catch (e) {
      return {  };
    }
  }

  /**
   * 获取拨测趋势数据（支持按地域/运营商维度下钻）
   */
  public async getDiagnoseTrend(req: IGetDiagnoseTrendReq): Promise<IGetDiagnoseTrendRsp> {
    try {
      const rsp = await this.trpcPost('GetDiagnoseTrend', req);

      if (!rsp?.trendData?.snapshots
        || req.dimension === DiagnoseTrendDimension.DIAGNOSE_TREND_DIMENSION_OVERALL) {
        return rsp;
      }

      // 第一步：收集所有唯一的 dimensionValue（从有值的 items 中提取）
      const allDimensionValues: string[] = [];
      rsp.trendData.snapshots.forEach((snapshot: any) => {
        snapshot?.items?.forEach((item: any) => {
          if (item?.dimensionValue && !allDimensionValues.includes(item.dimensionValue)) {
            allDimensionValues.push(item.dimensionValue);
          }
        });
      });

      // 第二步：补齐每个 snapshot 的 items，确保数量一致
      const transformedSnapshots = rsp.trendData.snapshots.map((snapshot: any) => {
        const existingItems = snapshot?.items || [];
        const firstItem = existingItems[0] || {};

        // 构建补齐后的 items
        const paddedItems = allDimensionValues.map((dimensionValue) => {
          // 查找是否已有该 dimensionValue 的 item
          const existingItem = existingItems.find((item: any) => item?.dimensionValue === dimensionValue);

          if (existingItem) {
            return { ...existingItem };
          }

          // 如果没有，创建一个默认值 item
          return {
            ...firstItem,
            dimensionValue,
            avgLatency: 0,
            maxLatency: 0,
            avgLossRate: 0,
            avgDnsDuration: 0,
            probeCount: 0,
            availabilityRate: 0,
          };
        });

        return {
          ...snapshot,
          items: paddedItems,
        };
      });
      // 返回改造后的数据
      return {
        ...rsp,
        trendData: {
          ...rsp.trendData,
          snapshots: transformedSnapshots,
        },
      };
    } catch (e) {
      return {};
    }
  }

  /**
   * 获取拨测详情列表（分页）
   */
  public async getDiagnoseList( // eslint-disable-line function-paren-newline
    req: IGetDiagnoseListReq,
  ): Promise<IGetDiagnoseListRsp> { // eslint-disable-line function-paren-newline
    try {
      return await this.trpcPost('GetDiagnoseList', req);
    } catch (e) {
      return {
      };
    }
  }

  /**
   * 获取拨测单条详情
   */
  public async getDiagnoseDetail(req: IGetDiagnoseDetailReq): Promise<IGetDiagnoseDetailRsp> {
    try {
      return await this.trpcPost('GetDiagnoseDetail', req);
    } catch (e) {
      return {  };
    }
  }

  /** 获取域名统计（最近30天上报量 + 归属） */
  public async getDomainStats(req: IGetDomainStatsReq): Promise<IGetDomainStatsRsp> {
    try {
      return await this.trpcPost<IGetDomainStatsRsp>('GetDomainStats', req);
    } catch (e) {
      console.log('getDomainStats', e);
      return {};
    }
  }
}

export const netHttpService = new NetHttpService();
